/**
 * Blockstream Activity Panel Component
 * Mock activity stream for displaying recent activities
 */

import React from 'react';
import { Button, Avatar, Tag } from 'antd';
import {
  CloseOutlined,
  ThunderboltOutlined,
  UserOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import './BlockstreamActivityPanel.css';

interface BlockstreamActivityPanelProps {
  isOpen: boolean;
  onClose: () => void;
  isInline?: boolean;
}

interface Activity {
  id: string;
  type: 'create' | 'update' | 'delete' | 'complete';
  user: {
    name: string;
    avatar?: string;
  };
  action: string;
  target: string;
  time: string;
  icon: React.ReactNode;
  color: string;
}

// Mock activities
const mockActivities: Activity[] = [
  {
    id: '1',
    type: 'create',
    user: { name: 'Marko Petrović' },
    action: 'created',
    target: 'new ticket #456',
    time: '5 minutes ago',
    icon: <PlusOutlined />,
    color: '#52c41a',
  },
  {
    id: '2',
    type: 'update',
    user: { name: 'Ana Jovanović' },
    action: 'updated',
    target: 'business partner "ACME Corp"',
    time: '15 minutes ago',
    icon: <EditOutlined />,
    color: '#1890ff',
  },
  {
    id: '3',
    type: 'complete',
    user: { name: 'Stefan Nikolić' },
    action: 'completed',
    target: 'task "Review documentation"',
    time: '30 minutes ago',
    icon: <CheckCircleOutlined />,
    color: '#52c41a',
  },
  {
    id: '4',
    type: 'create',
    user: { name: 'Milica Stojanović' },
    action: 'scheduled',
    target: 'meeting for tomorrow',
    time: '1 hour ago',
    icon: <ClockCircleOutlined />,
    color: '#faad14',
  },
  {
    id: '5',
    type: 'delete',
    user: { name: 'Petar Đorđević' },
    action: 'deleted',
    target: 'old attachment',
    time: '2 hours ago',
    icon: <DeleteOutlined />,
    color: '#ff4d4f',
  },
  {
    id: '6',
    type: 'update',
    user: { name: 'Jelena Popović' },
    action: 'commented on',
    target: 'ticket #123',
    time: '3 hours ago',
    icon: <EditOutlined />,
    color: '#1890ff',
  },
];

export const BlockstreamActivityPanel: React.FC<
  BlockstreamActivityPanelProps
> = ({ isOpen, onClose, isInline = false }) => {
  if (!isOpen) return null;

  return (
    <div
      className={`blockstream-activity-panel ${isInline ? 'blockstream-activity-inline' : ''}`}
    >
      {/* Header */}
      <div className="blockstream-activity-header">
        <div className="blockstream-activity-header-title">
          <ThunderboltOutlined className="blockstream-activity-icon" />
          <h3>Activity Stream</h3>
        </div>
        {!isInline && (
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onClose}
            className="blockstream-activity-close"
          />
        )}
      </div>

      {/* Activities List */}
      <div className="blockstream-activity-list">
        {mockActivities.map((activity) => (
          <div key={activity.id} className="activity-item">
            <div className="activity-icon" style={{ color: activity.color }}>
              {activity.icon}
            </div>
            <div className="activity-content">
              <div className="activity-main">
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  style={{ marginRight: 8 }}
                >
                  {activity.user.name.charAt(0)}
                </Avatar>
                <span className="activity-user">{activity.user.name}</span>
                <span className="activity-action">{activity.action}</span>
                <span className="activity-target">{activity.target}</span>
              </div>
              <div className="activity-time">{activity.time}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="blockstream-activity-footer">
        <Button type="link" block>
          View All Activities →
        </Button>
      </div>
    </div>
  );
};
